import { Labeled } from '@components/form/Labeled'
import type { Control, UseFormRegister } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import { DatePicker } from '@/components/ui/date-picker'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

import { UnitInput } from '../UnitInput'

import type { SettlementFormValues } from './useSettlementInputsForm'

type Name = keyof SettlementFormValues

/**
 * Every field carries a hint saying where the number comes from. Without it
 * the label alone ("Rozdelená zložka") does not tell you whether to type an
 * annual or a monthly amount, or which line of the statement it is.
 */
export function MoneyField({
  name,
  label,
  hint,
  suffix,
  placeholder,
  required,
  narrow,
  register
}: {
  name: Name
  label: string
  hint?: string
  suffix?: string
  /** Shown greyed when the field is empty — e.g. the value it falls back to. */
  placeholder?: string
  required?: boolean
  /** Short numeric fields do not need the full column width. */
  narrow?: boolean
  register: UseFormRegister<SettlementFormValues>
}) {
  return (
    <Labeled label={label} hint={hint} required={required}>
      <UnitInput
        unit={suffix}
        inputMode="decimal"
        placeholder={placeholder}
        className={cn(narrow && 'max-w-44')}
        {...register(name)}
      />
    </Labeled>
  )
}

export function DateField({
  name,
  label,
  hint,
  required,
  clearable,
  control
}: {
  name: Name
  label: string
  hint?: string
  required?: boolean
  clearable?: boolean
  control: Control<SettlementFormValues>
}) {
  return (
    <Labeled label={label} hint={hint} required={required}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DatePicker
            value={(field.value as string) || undefined}
            clearable={clearable}
            onChange={(value) => field.onChange(value ?? '')}
          />
        )}
      />
    </Labeled>
  )
}

export function SwitchField({
  name,
  label,
  hint,
  control
}: {
  name: Name
  label: string
  hint?: string
  control: Control<SettlementFormValues>
}) {
  return (
    <Labeled label={label} hint={hint}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Switch
            checked={!!field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
    </Labeled>
  )
}

/** Explanatory note above a group of fields. */
export function StepIntro({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 mb-5 rounded-xl bg-primary/[0.06] px-4 py-3 text-sm leading-relaxed text-ink">
      {children}
    </p>
  )
}
